import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  DialogContentText,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "../../api/Api.js";

// API Calls
const fetchCompanyCode = async () => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_NEW_DATA}/getCompanyCodes`
  );
  return response.data.companyCodes || [];
};

const fetchProjects = async () => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_NEW_DATA}/getProjects`
  );
  return response.data.projects || [];
};

const fetchProjectUsers = async (projectId) => {
  const response = await apiClient.get(
    `${process.env.REACT_APP_NEW_DATA}/getProjectUsers/${projectId}`
  );
  return response.data.users || [];
};

const addProject = async ({ projectName, companyCode }) => {
  const response = await apiClient.post(
    `${process.env.REACT_APP_AUTH_USER}/addnewproject`,
    {
      project_name: projectName,
      company_code: companyCode,
    }
  );
  if (response.status === 201) {
    toast.error(response.data.message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  }
  return response;
};

const updateProject = async ({ projectId, projectName, companyCode }) => {
  const project_id = projectId;
  return await apiClient.put(
    `${process.env.REACT_APP_NEW_DATA}/updateProject/${project_id}`,
    {
      project_name: projectName,
      company_code: companyCode,
    }
  );
};


const deleteProject = async (project_id) => {
  return await apiClient.delete(
    `${process.env.REACT_APP_NEW_DATA}/deleteProject/${project_id}`
  );
};

// Component
export default function AddProject() {
  const [projects, setProjects] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);

  const [openDialog, setOpenDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editProjectId, setEditProjectId] = useState(null);

  const [openUsersDialog, setOpenUsersDialog] = useState(false);
  const [projectUsers, setProjectUsers] = useState([]);
  const [selectedProjectName, setSelectedProjectName] = useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await fetchProjects();
      setProjects(data);
    } catch (err) {
      console.error("Failed loading projects:", err);
    }
  };

  const handleAddProjectClick = async () => {
    setEditMode(false);
    setNewProjectName("");
    setSelectedCompanyCode("");
    const codes = await fetchCompanyCode();
    setCompanyCodes(codes);
    setOpenDialog(true);
  };

  const handleEditProjectClick = async (project) => {
    setEditMode(true);
    setEditProjectId(project.project_id);
    setNewProjectName(project.project_name);
    setSelectedCompanyCode(project.company_code);
    const codes = await fetchCompanyCode();
    setCompanyCodes(codes);
    setOpenDialog(true);
  };

  const handleSubmitProject = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmAddOrEdit = async () => {
    try {
      if (editMode) {
        await updateProject({
          projectId: editProjectId,
          projectName: newProjectName,
          companyCode: selectedCompanyCode,
        });
      } else {
        await addProject({
          projectName: newProjectName,
          companyCode: selectedCompanyCode,
        });
      }

      setOpenDialog(false);
      setOpenConfirmDialog(false);
      setNewProjectName("");
      setSelectedCompanyCode("");
      setEditProjectId(null);
      await loadProjects();
    } catch (err) {
      console.error("Error saving project:", err);
    }
  };

  const handleCancelConfirm = () => setOpenConfirmDialog(false);

  const handleViewUsers = async (projectId, projectName) => {
    const users = await fetchProjectUsers(projectId);
    setSelectedProjectName(projectName);
    setProjectUsers(users);
    setOpenUsersDialog(true);
  };

  const handleCloseUsersDialog = () => {
    setOpenUsersDialog(false);
    setProjectUsers([]);
  };

  const handleDeleteClick = (projectId) => {
    setProjectToDelete(projectId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProject(projectToDelete);
      setOpenDeleteDialog(false);
      await loadProjects();
    } catch (err) {
      console.error("Error deleting project:", err);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h4">Projects</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddProjectClick}
        >
          Add New Project
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project ID</TableCell>
              <TableCell>Company Code</TableCell>
              <TableCell>Project Name</TableCell>
              <TableCell>Created Time</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Edit</TableCell>
              <TableCell>Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.length > 0 ? (
              projects.map((project) => (
                <TableRow key={project.project_id}>
                  <TableCell>{project.project_id}</TableCell>
                  <TableCell>{project.company_code}</TableCell>
                  <TableCell>{project.project_name}</TableCell>
                  <TableCell>{project.createdAt}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() =>
                        handleViewUsers(
                          project.project_id,
                          project.project_name
                        )
                      }
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleEditProjectClick(project)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleDeleteClick(project.project_id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No Projects Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        fullScreen={fullScreen}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogTitle>
          {editMode ? "Edit Project" : "Add New Project"}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Company Code</InputLabel>
            <Select
              value={selectedCompanyCode}
              onChange={(e) => setSelectedCompanyCode(e.target.value)}
              label="Company Code"
            >
              {companyCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Project Name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitProject}
            variant="contained"
            disabled={!newProjectName || !selectedCompanyCode}
          >
            {editMode ? "Update" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Add/Edit Dialog */}
      <Dialog open={openConfirmDialog} onClose={handleCancelConfirm}>
        <DialogTitle>Confirm {editMode ? "Update" : "Add"} Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {editMode ? (
              <>
                Update project <b>{newProjectName}</b> for company code{" "}
                <b>{selectedCompanyCode}</b>?
              </>
            ) : (
              <>
                Add project <b>{newProjectName}</b> for company code{" "}
                <b>{selectedCompanyCode}</b>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>Cancel</Button>
          <Button
            onClick={handleConfirmAddOrEdit}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Dialog */}
      <Dialog
        fullScreen={fullScreen}
        open={openUsersDialog}
        onClose={handleCloseUsersDialog}
      >
        <DialogTitle>Users in {selectedProjectName}</DialogTitle>
        <DialogContent>
          {projectUsers.length ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectUsers.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.user_id}</TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No users found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsersDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this project?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
